import { ampli } from '~/domain/plugins/analytics';
import type { UpcomingEventCard } from './page.server';

/**
 * Amplitude events for the upcoming-events page, ported from the legacy page
 * with **identical names and property payloads** so the existing funnels and
 * charts survive the migration. Keep the shapes as they are; a renamed or
 * dropped property silently breaks a saved chart rather than failing here.
 *
 * Note that Amplitude's `Element Clicked` autocapture was switched off in
 * PR #1122, so these explicit events are the only signal for this page.
 * `Page Viewed` is unaffected.
 */

/** The canonical filter keys, in the order the legacy page reported them. */
const FILTER_KEYS = [
  'startDate',
  'experiences',
  'locations',
  'languages',
  'tags',
] as const;

/**
 * Which filters are actually narrowing the list.
 *
 * The legacy page derived this from the keys present on its filter object, so
 * a bare URL reported zero. `from` counts only when the visitor set it — it
 * always has a value once the loader defaults it, and counting that default
 * would report one filter on every unfiltered page view.
 */
export function activeFilterKeys(searchParams: URLSearchParams): string[] {
  return FILTER_KEYS.filter((key) => searchParams.getAll(key).some(Boolean));
}

export function trackPageViewed(searchParams: URLSearchParams): void {
  const used = activeFilterKeys(searchParams);
  ampli.upcomingEventsPageViewed({
    number_of_filters_used: used.length,
    list_of_filters_used: used.join(', '),
  });
}

export function trackDatePickerOpened(): void {
  ampli.upcomingEventsDatePickerOpened();
}

export function trackDateSelected(date: string): void {
  ampli.upcomingEventsPickerDateSelected({
    days_from_current_date: wholeDaysFromToday(date),
  });
}

export function trackExperienceFilterOpened(): void {
  ampli.upcomingEventsExperienceFilterOpened();
}

export function trackExperienceFilterApplied(
  selected: { id: string; name: string }[]
): void {
  ampli.upcomingEventsExperienceFilterApplied({
    list_experience_id: selected.map((e) => e.id).join(', '),
    list_experience_name: selected.map((e) => e.name).join(', '),
    number_of_experiences: selected.length,
  });
}

export function trackLocationFilterOpened(): void {
  ampli.upcomingEventsLocationFilterOpened();
}

export function trackLocationFilterApplied(
  selected: { id: string; label: string }[]
): void {
  ampli.upcomingEventsLocationFilterApplied({
    list_of_locations: selected.map((l) => l.label).join(', '),
    number_of_locations: selected.length,
  });
}

export function trackLanguageFilterOpened(): void {
  ampli.upcomingEventsLanguageFilterOpened();
}

export function trackLanguageFilterApplied(
  selected: { id: string; label: string }[]
): void {
  ampli.upcomingEventsLanguageFilterApplied({
    language_list: selected.map((l) => l.label).join(', '),
    number_of_languages: selected.length,
  });
}

export function trackBookClicked(event: TrackedEvent, index: number): void {
  ampli.upcomingEventsBookClicked(eventProperties(event, index));
}

export function trackReadMoreClicked(event: TrackedEvent, index: number): void {
  ampli.upcomingEventsReadMoreClicked(eventProperties(event, index));
}

/** Only the fields the payload reads, so the card's view model can change
 *  shape without dragging tracking along. */
type TrackedEvent = Pick<
  UpcomingEventCard,
  'startTime' | 'eventId' | 'experienceId' | 'experienceName' | 'locales'
> & { location?: UpcomingEventCard['location'] };

/** The per-event payload both card events share. `event_index` is 1-based,
 *  matching the legacy page. */
function eventProperties(event: TrackedEvent, index: number) {
  return {
    days_until_event: wholeDaysFromToday(event.startTime),
    event_id: event.eventId,
    event_index: index + 1,
    event_scheduled_date: event.startTime,
    experience_id: event.experienceId,
    experience_name: event.experienceName,
    language_list: event.locales.join(),
    location: event.location?.displayName.join(', ') ?? '',
  };
}

/** Whole days from today to `date`, both truncated to local midnight so a
 *  same-day event reports 0 rather than a fraction. Mirrors the legacy page's
 *  `differenceInDays(startOfDay(...), startOfDay(new Date()))`. */
function wholeDaysFromToday(date: string): number {
  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round(
    (startOfDay(new Date(date)) - startOfDay(new Date())) / MS_PER_DAY
  );
}
